/**
 * CORNER_V2: Corner-owned PDF signing viewer.
 * Renders PDFs via pdf.js canvas with field overlays and a thumbnail rail.
 * Fully driven by SignatureField[] + mode (sign/configure) + current signer context.
 *
 * Data flow:
 *   SignatureField[] → buildFieldDescriptors() → FieldDescriptor[]
 *     → buildPageFieldIndex() → PageFieldInfo[]
 *       → PdfPageCanvas (per page) → FieldOverlay → FieldMarker
 *       → ThumbnailRail → ThumbnailPage (with progress badges)
 *     → findNextPendingField() → NextFieldBanner + toolbar "Next field"
 */
import { useState, useCallback, useRef, useMemo, useEffect, type CSSProperties } from 'react';
import { signingFieldSizeForKind, type CommentPinData, type SignatureField } from '@corner/shared';
import { usePdfDocument } from './usePdfDocument';
import PdfPageCanvas from './PdfPageCanvas';
import ThumbnailRail from './ThumbnailRail';
import PdfViewerToolbar from './PdfViewerToolbar';
import NextFieldBanner from './NextFieldBanner';
import SlotInspectorPanel from './SlotInspectorPanel';
import type { SlotUpdate, Party } from './SlotInspectorPanel';
import { SaveToTemplatePrompt } from './SaveToTemplatePrompt';
import { PDF_VIEWER } from './pdfViewerTokens';
import { APP } from '../app/tokens';
import type { ViewerMode, FieldDescriptor, ViewerSigner } from './signingTypes';
import { buildFieldDescriptors, buildPageFieldIndex, findNextPendingField } from './signingTypes';
import RecipientLegend from './RecipientLegend';
import { DeleteFieldConfirm } from './DeleteFieldConfirm';
import { colorForSigner } from '../../lib/signerColors';
import AddFieldToolbar, { type PlacementType } from './AddFieldToolbar';

export interface PdfSigningViewerProps {
  pdfUrl: string;
  /** Runtime guard: this viewer only supports PDFs. */
  mimeType?: string;
  /** Stable identifier for this document. Used as the key in the placeholder
   *  values store so live overlay edits stay associated with the right doc. */
  documentId?: string;
  getToken?: () => string | null;
  /** SignatureField[] — the canonical field data from SigningTokenInfo or esign result. */
  fields?: SignatureField[];
  /** Which field is currently active (by ID). */
  activeFieldId?: string | null;
  /** Fired when user clicks a field overlay. Returns field ID. */
  onFieldClick?: (fieldId: string) => void;
  /** Fired when user clicks "Next field" navigation. */
  onNextField?: () => void;
  /** Current signer identifier (used to determine self vs other fields). */
  currentSignerId?: string;
  /** Viewer mode: 'sign' (signer UX) or 'configure' (field placement / admin). */
  mode?: ViewerMode;
  signerName?: string;
  signaturePreview?: string | null;
  initialPage?: number;
  showThumbnails?: boolean;
  isMobile?: boolean;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  /** Fired when all fields for the current signer are completed. */
  onAllFieldsComplete?: () => void;
  /** Parties for the inspector dropdown (configure mode). */
  parties?: Party[];
  /** Fired when a slot is updated via the inspector panel (configure mode). */
  onSlotUpdate?: (slotId: string, updates: SlotUpdate) => void;

  // ── Template memory (UI-first; vertical 4 wires the persist call) ─
  /** Display name of the template this document is linked to. When set,
   *  field moves trigger the SaveToTemplatePrompt asking whether to
   *  persist the move to the template too. Omit to keep current behaviour
   *  (drags update only the local instance). */
  linkedTemplateName?: string;
  /** Fired when the user clicks "Save to template" on the prompt.
   *  Parent persists the field bbox change to the template's fieldMap. */
  onSaveFieldToTemplate?: (input: {
    fieldId: string;
    rect: { x: number; y: number; width: number; height: number };
  }) => void | Promise<void>;

  // ── v1 placement-redesign: human-first marker editing ─────────────
  /** Recipient list. Drives the RecipientLegend sidebar + per-signer
   *  marker colors. When omitted, viewer falls back to single-signer
   *  legacy palette and the legend is hidden. Configure mode only. */
  signers?: ViewerSigner[];
  /** Index into `signers` for the active recipient — new placements
   *  attach to this signer. Defaults to 0. */
  activeSignerIndex?: number;
  onSelectSigner?: (index: number) => void;
  /** Optional controlled placement mode shared with chat signing guide. */
  placementMode?: PlacementType | null;
  onPlacementModeChange?: (mode: PlacementType | null) => void;
  /** Append a new field to the working set. Fired after a click in
   *  placement mode. */
  onAddField?: (field: SignatureField) => void;
  /** Remove a field from the working set. Fired from the × confirm popover. */
  onDeleteField?: (fieldId: string) => void;
  /** Flip a field's state from 'suggested' to 'confirmed' (orange→green)
   *  after a drag. */
  onConfirmField?: (fieldId: string) => void;
  /** Generic field-move handler used by the new working-set flow.
   *  When provided, the viewer uses this in addition to (or instead
   *  of) onSlotUpdate for the rect change. */
  onMoveField?: (fieldId: string, rect: { x: number; y: number; width: number; height: number }) => void;
  /** Final human-confirmed self-sign action. In configure mode this is
   *  the explicit boundary between reviewing markers and embedding the
   *  user's saved signature into the PDF. */
  onApplySignature?: () => void;
  applySignatureDisabled?: boolean;
  /** Invites other signers — shown on the recipient legend footers. */
  onSendForSignature?: () => void;
  sendForSignatureDisabled?: boolean;
  /** Add recipient email from legend empty-state (syncs with workspace signer list). */
  onAddRecipientEmail?: (email: string) => void;
  /** Select PDF text and send excerpt to parent (e.g. Ask Corner in chat). */
  onAskCornerAboutSelection?: (snippet: { text: string; page: number }) => void;
  /** Page-anchored document comments. Rendered inside each PDF page so pins scroll with content. */
  commentPins?: CommentPinData[];
  activeCommentPinId?: string | null;
  onCommentPinClick?: (pinId: string) => void;
  onCommentPinMove?: (pinId: string, xPercent: number, yPercent: number) => void;
  commentMode?: boolean;
  pendingComment?: { xPercent: number; yPercent: number; pageNumber: number; quote?: string } | null;
  onDocumentCommentClick?: (xPercent: number, yPercent: number, pageNumber: number) => void;
  onCommentSubmit?: (body: string) => void;
  onCommentCancel?: () => void;
  /**
   * When false with the new placement UX (+ Signature / + Date / …), those
   * controls are omitted from the PDF toolbar — e.g. main workspace drives
   * placement from the chat signing guide. Defaults true so wizards without
   * chat (workflow template review) keep the toolbar.
   */
  inlinePlacementToolbar?: boolean;
}

const ZOOM_STEP = 25;
const ZOOM_MIN = 50;
const ZOOM_MAX = 300;
const FALLBACK_ZOOM = 140;
const FIT_WIDTH_MAX = 155; // never auto-zoom beyond this — keeps text comfortable

export default function PdfSigningViewer({
  mimeType,
  ...props
}: PdfSigningViewerProps) {
  const normalizedMime = (mimeType ?? '').toLowerCase();
  if (normalizedMime && normalizedMime !== 'application/pdf' && !normalizedMime.includes('/pdf')) {
    console.warn('[PdfSigningViewer] non-pdf-skipped', {
      mimeType,
      documentId: props.documentId ?? null,
      pdfUrl: props.pdfUrl,
    });
    return (
      <div
        style={{
          width: '100%',
          minHeight: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          backgroundColor: APP.bgDocViewer,
          color: APP.textSecondary,
          fontFamily: APP.fontFamily,
          fontSize: 13,
          lineHeight: '19px',
        }}
      >
        This file is not a PDF, so the PDF signing viewer was not opened.
      </div>
    );
  }
  return <PdfSigningViewerInner {...props} mimeType={mimeType} />;
}

function PdfSigningViewerInner({
  pdfUrl, documentId, getToken, fields = [], activeFieldId: externalActiveFieldId,
  onFieldClick, onNextField, currentSignerId, mode = 'sign',
  signerName: _signerName, signaturePreview, initialPage, showThumbnails = true, isMobile = false,
  zoom: controlledZoom, onZoomChange, onAllFieldsComplete,
  parties = [], onSlotUpdate,
  linkedTemplateName, onSaveFieldToTemplate,
  // v1 placement-redesign:
  signers, activeSignerIndex = 0, onSelectSigner,
  placementMode: controlledPlacementMode, onPlacementModeChange,
  onAddField, onDeleteField, onConfirmField, onMoveField,
  onApplySignature, applySignatureDisabled,
  onSendForSignature,
  sendForSignatureDisabled,
  onAddRecipientEmail,
  onAskCornerAboutSelection,
  commentPins,
  activeCommentPinId,
  onCommentPinClick,
  onCommentPinMove,
  commentMode,
  pendingComment,
  onDocumentCommentClick,
  onCommentSubmit,
  onCommentCancel,
  inlinePlacementToolbar = true,
}: PdfSigningViewerProps) {
  // v1 placement-redesign: when a recipient list is wired AND we're in
  // configure mode, the viewer assumes the new flow (per-signer colors,
  // add/delete buttons, sidebar legend). Otherwise it falls back to the
  // legacy SlotInspectorPanel UX so existing callers don't break.
  const useNewPlacementUx = mode === 'configure' && Array.isArray(signers);
  const delegatePlacementToChat = useNewPlacementUx && !inlinePlacementToolbar;
  const showPlacementRail = useNewPlacementUx && !delegatePlacementToChat && !isMobile && !!onAddField;
  // Fallback: if no explicit documentId, derive from pdfUrl so the placeholder
  // values store key is still stable per-doc.
  const resolvedDocumentId = documentId ?? pdfUrl;
  const { document, numPages, loading, error } = usePdfDocument(pdfUrl, getToken);
  const [internalZoom, setInternalZoom] = useState(FALLBACK_ZOOM);
  const [activePage, setActivePage] = useState(initialPage ?? 1);
  const [internalActiveFieldId, setInternalActiveFieldId] = useState<string | null>(null);
  const [inspectedSlot, setInspectedSlot] = useState<FieldDescriptor | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const activeFieldId = externalActiveFieldId ?? internalActiveFieldId;

  const zoom = controlledZoom ?? internalZoom;
  const setZoom = useCallback((z: number) => {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
    if (onZoomChange) onZoomChange(clamped);
    else setInternalZoom(clamped);
  }, [onZoomChange]);

  const scale = zoom / 100;

  // ── Fit-to-width: auto-calculate zoom so PDF fills the container ──
  const hasAutoFitted = useRef(false);
  useEffect(() => {
    hasAutoFitted.current = false;
  }, [pdfUrl]);

  useEffect(() => {
    if (hasAutoFitted.current || !document || controlledZoom != null) return;
    const el = scrollerRef.current;
    if (!el) return;
    hasAutoFitted.current = true;
    document.getPage(1).then((page) => {
      const naturalWidth = page.getViewport({ scale: 1 }).width;
      const availableWidth = el.clientWidth - 32; // 16px padding on each side
      if (naturalWidth > 0 && availableWidth > 0) {
        const fitZoom = Math.round((availableWidth / naturalWidth) * 100);
        setInternalZoom(Math.max(ZOOM_MIN, Math.min(FIT_WIDTH_MAX, fitZoom)));
      }
    });
  }, [document, controlledZoom, pdfUrl]);

  // ── Signing data derivation ────────────────────────────────────
  const descriptors = useMemo(
    () => buildFieldDescriptors(fields, currentSignerId, signers, numPages > 0 ? numPages : null),
    [fields, currentSignerId, signers, numPages],
  );
  const showSequentialSigning = mode === 'sign';

  // ── Drag-to-move: local rect overrides for configure mode ────
  const [rectOverrides, setRectOverrides] = useState<Map<string, { x: number; y: number; width: number; height: number }>>(new Map());

  // Apply local drag overrides to descriptors (so moved markers render at new positions)
  const effectiveDescriptors = useMemo(() => {
    if (rectOverrides.size === 0) return descriptors;
    return descriptors.map((d) => {
      const override = rectOverrides.get(d.id);
      return override ? { ...d, rect: override } : d;
    });
  }, [descriptors, rectOverrides]);

  const pageFieldIndex = useMemo(
    () => buildPageFieldIndex(effectiveDescriptors, numPages),
    [effectiveDescriptors, numPages],
  );

  // Next pending field for the current signer
  const nextField = useMemo(
    () => findNextPendingField(descriptors, activeFieldId ?? undefined, mode),
    [descriptors, activeFieldId, mode],
  );

  // Check if all signer fields are complete
  const allSignerFieldsComplete = useMemo(() => {
    const signerFields = descriptors.filter((f) => f.isForCurrentSigner);
    return signerFields.length > 0 && signerFields.every((f) => f.signed);
  }, [descriptors]);

  // Signing progress for current signer
  const signingProgress = useMemo(() => {
    const signerFields = descriptors.filter((f) => f.isForCurrentSigner);
    const signed = signerFields.filter((f) => f.signed).length;
    return { signed, total: signerFields.length };
  }, [descriptors]);

  // ── Handlers ───────────────────────────────────────────────────
  const scrollToPage = useCallback((page: number) => {
    const el = scrollerRef.current?.querySelector(`[data-page="${page}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handlePageVisible = useCallback((page: number) => {
    setActivePage(page);
  }, []);

  const handleFieldClick = useCallback((fieldId: string) => {
    setInternalActiveFieldId(fieldId);
    onFieldClick?.(fieldId);
  }, [onFieldClick]);

  const handleNextField = useCallback(() => {
    if (nextField) {
      setInternalActiveFieldId(nextField.id);
      scrollToPage(nextField.page);
    }
    onNextField?.();
  }, [nextField, onNextField, scrollToPage]);

  const handleFieldInspect = useCallback((field: FieldDescriptor) => {
    setInspectedSlot(field);
  }, []);

  const handleSlotUpdate = useCallback((slotId: string, updates: SlotUpdate) => {
    onSlotUpdate?.(slotId, updates);
    setInspectedSlot(null);
  }, [onSlotUpdate]);

  // Tracks the most-recently-moved field so the SaveToTemplatePrompt
  // knows which one to reference. Replaced by each subsequent drag —
  // a new drag dismisses the prior unanswered prompt (the prior move
  // stays as a local-only override, which matches the safer default).
  const [pendingTemplateMove, setPendingTemplateMove] = useState<{
    fieldId: string;
    label: string;
    rect: { x: number; y: number; width: number; height: number };
  } | null>(null);

  // v1 placement-redesign: which kind of marker the next page-click will
  // drop. null = pointer is in normal interaction mode. Esc cancels.
  const [internalPlacementMode, setInternalPlacementMode] = useState<PlacementType | null>(null);
  const placementMode = controlledPlacementMode !== undefined ? controlledPlacementMode : internalPlacementMode;
  const updatePlacementMode = useCallback((next: PlacementType | null) => {
    if (controlledPlacementMode !== undefined) {
      onPlacementModeChange?.(next);
    } else {
      setInternalPlacementMode(next);
    }
  }, [controlledPlacementMode, onPlacementModeChange]);
  // v1 placement-redesign: the field whose × was just clicked, plus the
  // marker's bounding rect so DeleteFieldConfirm can anchor itself.
  const [pendingDelete, setPendingDelete] = useState<{ fieldId: string; label: string; anchorRect: DOMRect } | null>(null);

  // Esc cancels any pending placement (pointer back to normal) and any
  // open delete confirm.
  useEffect(() => {
    if (!useNewPlacementUx) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (placementMode) updatePlacementMode(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [useNewPlacementUx, placementMode, updatePlacementMode]);

  const handleTogglePlacement = useCallback((type: PlacementType) => {
    updatePlacementMode(placementMode === type ? null : type);
  }, [placementMode, updatePlacementMode]);

  const handleRequestDelete = useCallback((fieldId: string) => {
    // Resolve the marker DOM node so the popover can anchor next to it.
    const node = window.document.querySelector(`[data-field-id="${CSS.escape(fieldId)}"]`)
      ?? window.document.querySelector(`[title="${CSS.escape(descriptors.find((d) => d.id === fieldId)?.label ?? '')}"]`);
    const rect = (node as HTMLElement | null)?.getBoundingClientRect()
      ?? new DOMRect(window.innerWidth / 2, window.innerHeight / 2, 0, 0);
    const label = descriptors.find((d) => d.id === fieldId)?.label ?? 'field';
    setPendingDelete({ fieldId, label, anchorRect: rect });
  }, [descriptors]);

  const handleConfirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    onDeleteField?.(pendingDelete.fieldId);
    setPendingDelete(null);
  }, [pendingDelete, onDeleteField]);

  // Build a SignatureField for a click at (xPct, yPct) on a given page.
  // Defaults match what the wizard preview expects — comfortable sizes
  // for each field type, percent-based.
  const handlePagePlacementClick = useCallback((page: number, xPct: number, yPct: number) => {
    if (!placementMode || !onAddField || !signers || signers.length === 0) return;
    const signer = signers[Math.max(0, Math.min(activeSignerIndex, signers.length - 1))];
    if (!signer) return;
    const sizeByType: Record<PlacementType, { width: number; height: number; suffix: string; type: SignatureField['type'] }> = {
      signature: { ...signingFieldSizeForKind('signature'), suffix: 'signature', type: 'signature' },
      date:      { ...signingFieldSizeForKind('date'),      suffix: 'date',      type: 'date' },
      name:      { ...signingFieldSizeForKind('name'),      suffix: 'name',      type: 'text' },
      initial:   { ...signingFieldSizeForKind('initial'),   suffix: 'initials',  type: 'initials' },
      title:     { ...signingFieldSizeForKind('title'),     suffix: 'title',     type: 'text' },
      email:     { width: 18, height: 2.8,                  suffix: 'email',     type: 'text' },
    };
    const cfg = sizeByType[placementMode];
    // Anchor the marker so the click point sits at its top-left, but
    // clamp so the marker doesn't run off the page edge.
    const x = Math.max(0, Math.min(100 - cfg.width, xPct));
    const y = Math.max(0, Math.min(100 - cfg.height, yPct));
    const forRecipient = signer.role === 'other';
    const newField: SignatureField = {
      id: typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `field-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: cfg.type,
      page,
      x, y,
      width: cfg.width,
      height: cfg.height,
      label: `${signer.name || signer.role || 'Signer'} ${placementMode}`,
      role: `${signer.role}_${cfg.suffix}`,
      printedText: placementMode === 'email' ? signer.email : undefined,
      signedByEmail: signer.email,
      party: forRecipient ? 'other' : 'self',
      signer: forRecipient ? 'other' : 'you',
      assignedSignerIndex: activeSignerIndex,
      state: placementMode === 'title' ? ('suggested' as const) : ('confirmed' as const),
    };
    onAddField(newField);
    updatePlacementMode(null);
  }, [placementMode, onAddField, signers, activeSignerIndex, updatePlacementMode]);

  const handleFieldMove = useCallback((fieldId: string, rect: { x: number; y: number; width: number; height: number }) => {
    // Capture original position before overriding (for AI learning loop)
    const descriptor = descriptors.find((d) => d.id === fieldId);
    const originalRect = descriptor?.rect;

    setRectOverrides((prev) => new Map(prev).set(fieldId, rect));
    onSlotUpdate?.(fieldId, { rect });
    // v1 placement-redesign: parent owns the working set; mirror the move there.
    onMoveField?.(fieldId, rect);

    // Record correction for AI learning (fire-and-forget). This is the
    // pre-template-memory telemetry; it stays as the universal logger
    // even when the doc is template-linked.
    if (originalRect) {
      const apiBase = import.meta.env.VITE_API_URL || '';
      fetch(`${apiBase}/api/signing/field-corrections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileId: fieldId.split('_')[0] ?? fieldId,
          fieldId,
          fieldType: descriptor?.type ?? 'signature',
          originalBbox: originalRect,
          correctedBbox: rect,
          labelText: descriptor?.label ?? '',
        }),
      }).catch(() => {}); // Non-blocking
    }

    // Surface the "save to template?" prompt only when the doc is
    // linked to a template AND the parent has wired the persist call.
    if (linkedTemplateName && onSaveFieldToTemplate) {
      setPendingTemplateMove({
        fieldId,
        label: descriptor?.label ?? 'this field',
        rect,
      });
    }
  }, [onSlotUpdate, descriptors, linkedTemplateName, onSaveFieldToTemplate, onMoveField]);

  // CORNER_V2: AI guidance — auto-jump to first pending field on initial load
  const hasAutoJumped = useRef(false);
  useEffect(() => {
    if (hasAutoJumped.current || !document || mode !== 'sign' || descriptors.length === 0) return;
    const firstPending = findNextPendingField(descriptors, undefined, 'sign');
    if (firstPending) {
      hasAutoJumped.current = true;
      setInternalActiveFieldId(firstPending.id);
      setTimeout(() => scrollToPage(firstPending.page), 300);
    }
  }, [document, descriptors, mode, scrollToPage]);

  // ── Loading / error states ─────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', backgroundColor: PDF_VIEWER.bgViewer, color: APP.textMuted, fontSize: 14 }}>
        Loading PDF...
      </div>
    );
  }
  if (error || !document) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', backgroundColor: PDF_VIEWER.bgViewer, color: APP.textSecondary, fontSize: 14 }}>
        {error ?? 'Unable to load PDF'}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, backgroundColor: PDF_VIEWER.bgViewer }}>
      {/* Toolbar */}
      <div style={{ flexShrink: 0 }}>
        <PdfViewerToolbar
          currentPage={activePage}
          numPages={numPages}
          zoom={zoom}
          onZoomIn={() => setZoom(zoom + ZOOM_STEP)}
          onZoomOut={() => setZoom(zoom - ZOOM_STEP)}
          onFitWidth={() => {
            // Recalculate fit-to-width
            if (!document || !scrollerRef.current) return;
            document.getPage(1).then((page) => {
              const naturalWidth = page.getViewport({ scale: 1 }).width;
              const availableWidth = scrollerRef.current!.clientWidth - (isMobile ? 16 : 32);
              if (naturalWidth > 0 && availableWidth > 0) {
                setZoom(Math.max(ZOOM_MIN, Math.min(FIT_WIDTH_MAX, Math.round((availableWidth / naturalWidth) * 100))));
              }
            });
          }}
          showNextField={showSequentialSigning && !!nextField && !allSignerFieldsComplete}
          nextFieldLabel={nextField?.label}
          onNextField={handleNextField}
          placementMode={delegatePlacementToChat || showPlacementRail ? undefined : (useNewPlacementUx ? placementMode : undefined)}
          onTogglePlacement={delegatePlacementToChat || showPlacementRail ? undefined : (useNewPlacementUx && onAddField ? handleTogglePlacement : undefined)}
          activeSignerColor={delegatePlacementToChat || showPlacementRail ? undefined : (useNewPlacementUx && signers && signers[activeSignerIndex]
            ? colorForSigner(activeSignerIndex)
            : undefined)}
          addFieldDisabled={delegatePlacementToChat || showPlacementRail ? undefined : (useNewPlacementUx && (!signers || signers.length === 0))}
          onApplySignature={delegatePlacementToChat || showPlacementRail ? undefined : onApplySignature}
          applySignatureDisabled={delegatePlacementToChat || showPlacementRail ? undefined : applySignatureDisabled}
        />
      </div>

      {/* CORNER_V2: AI guidance — signing progress bar (sign mode only) */}
      {mode === 'sign' && signingProgress.total > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '6px 14px', borderBottom: `1px solid ${APP.border}`,
          backgroundColor: APP.bgContent, flexShrink: 0,
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: allSignerFieldsComplete ? PDF_VIEWER.fieldSigned : PDF_VIEWER.fieldUnsigned }}>
            {signingProgress.signed} of {signingProgress.total} fields signed
          </span>
          <div style={{ flex: 1, height: 4, borderRadius: 2, backgroundColor: `${APP.border}` }}>
            <div style={{
              width: `${(signingProgress.signed / signingProgress.total) * 100}%`,
              height: '100%', borderRadius: 2,
              backgroundColor: allSignerFieldsComplete ? PDF_VIEWER.fieldSigned : PDF_VIEWER.fieldUnsigned,
              transition: 'width 0.3s ease, background-color 0.3s ease',
            }} />
          </div>
        </div>
      )}

      {/* Body: viewer + thumbnails */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {showPlacementRail && (
          <aside style={FIELD_RAIL}>
            <div style={FIELD_RAIL_HEADER}>
              <span style={FIELD_RAIL_TITLE}>Fields</span>
              <span style={FIELD_RAIL_HINT}>
                Pick a field, then click the PDF.
              </span>
            </div>
            {signers?.[activeSignerIndex] && (
              <div style={FIELD_RAIL_SIGNER}>
                <span
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: colorForSigner(activeSignerIndex).outline,
                    flexShrink: 0,
                  }}
                />
                <span style={FIELD_RAIL_SIGNER_TEXT}>
                  {signers[activeSignerIndex].role === 'other' ? 'Recipient' : 'You'} · {signers[activeSignerIndex].name || signers[activeSignerIndex].email}
                </span>
              </div>
            )}
            <div style={FIELD_RAIL_SCROLL}>
              <AddFieldToolbar
                placementMode={placementMode ?? null}
                onTogglePlacement={handleTogglePlacement}
                activeSignerColor={signers && signers[activeSignerIndex]
                  ? colorForSigner(activeSignerIndex)
                  : undefined}
                disabled={!signers || signers.length === 0}
                variant="rail"
              />
            </div>
            {onApplySignature && (
              <div style={FIELD_RAIL_ACTIONS}>
                <button
                  type="button"
                  onClick={onApplySignature}
                  disabled={applySignatureDisabled}
                  title={applySignatureDisabled ? 'Place at least one marker first' : 'Apply your signature, initials, date, name, title, and email markers'}
                  style={{
                    ...FIELD_RAIL_APPLY_BTN,
                    ...(applySignatureDisabled ? FIELD_RAIL_APPLY_BTN_DISABLED : {}),
                  }}
                >
                  Apply changes
                </button>
                <span style={FIELD_RAIL_ACTION_HINT}>
                  Applies your fields into a new PDF version.
                </span>
              </div>
            )}
          </aside>
        )}
        {useNewPlacementUx && placementMode && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              maxWidth: 'calc(100% - 32px)',
              padding: '8px 12px',
              borderRadius: 999,
              border: `1px solid ${APP.borderCard}`,
              background: APP.bgCardBody,
              color: APP.textPrimary,
              boxShadow: APP.inputShadow,
              fontFamily: APP.fontFamily,
              fontSize: 12,
              fontWeight: 750,
              lineHeight: '16px',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: signers?.[activeSignerIndex] ? colorForSigner(activeSignerIndex).outline : APP.bgChatSendBtn,
                flexShrink: 0,
              }}
            />
            {`Placing ${signers?.[activeSignerIndex]?.role === 'other' ? `${signers?.[activeSignerIndex]?.name || 'recipient'}'s` : 'your'} ${placementMode}. Click the document.`}
          </div>
        )}
        {/* Page scroller */}
        <div
          ref={scrollerRef}
          style={{
            flex: 1, overflowY: 'auto', overflowX: 'auto',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: isMobile ? 10 : PDF_VIEWER.pageGap,
            padding: isMobile ? '10px 8px 86px' : `${PDF_VIEWER.pageGap}px 16px`,
          }}
        >
          {pageFieldIndex.map((pageInfo) => (
            <PdfPageCanvas
              key={pageInfo.pageNumber}
              document={document}
              pageNumber={pageInfo.pageNumber}
              scale={scale}
              fields={pageInfo.allFields}
              activeFieldId={activeFieldId}
              inspectedFieldId={inspectedSlot?.id}
              mode={mode}
              signaturePreview={signaturePreview}
              documentId={resolvedDocumentId}
              onFieldClick={handleFieldClick}
              onFieldInspect={mode === 'configure' && !useNewPlacementUx ? handleFieldInspect : undefined}
              onFieldMove={handleFieldMove}
              onVisible={handlePageVisible}
              // v1 placement-redesign: wire delete + state-confirm + per-signer
              // colors + page-click placement hand-off.
              onRequestDelete={useNewPlacementUx && onDeleteField ? handleRequestDelete : undefined}
              onConfirmField={useNewPlacementUx && onConfirmField ? onConfirmField : undefined}
              useSignerPalette={useNewPlacementUx}
              onPagePlacementClick={useNewPlacementUx && placementMode ? handlePagePlacementClick : undefined}
              placementArmed={useNewPlacementUx && placementMode != null}
              enableTextSelection={!!onAskCornerAboutSelection}
              onAskCornerAboutSelection={onAskCornerAboutSelection}
              commentPins={commentPins}
              activeCommentPinId={activeCommentPinId}
              onCommentPinClick={onCommentPinClick}
              onCommentPinMove={onCommentPinMove}
              commentMode={commentMode}
              pendingComment={pendingComment}
              onDocumentCommentClick={onDocumentCommentClick}
              onCommentSubmit={onCommentSubmit}
              onCommentCancel={onCommentCancel}
            />
          ))}
        </div>

        {/* Right-rail panel:
            - new placement UX (configure + signers wired): RecipientLegend
            - legacy configure UX (slot inspector or no thumbnails): SlotInspectorPanel
            - everything else: thumbnails (when enabled). */}
        {useNewPlacementUx && signers && !isMobile ? (
          <RecipientLegend
            signers={signers}
            fields={effectiveDescriptors}
            activeSignerIndex={activeSignerIndex}
            onSelectSigner={onSelectSigner ?? (() => {})}
            onSendForSignature={onSendForSignature}
            sendForSignatureDisabled={sendForSignatureDisabled}
            onAddRecipientEmail={onAddRecipientEmail}
          />
        ) : mode === 'configure' && !isMobile && (inspectedSlot || !showThumbnails) ? (
          <SlotInspectorPanel
            slot={inspectedSlot}
            parties={parties}
            onClose={() => setInspectedSlot(null)}
            onUpdate={handleSlotUpdate}
          />
        ) : showThumbnails ? (
          <ThumbnailRail
            document={document}
            numPages={numPages}
            pageFieldIndex={pageFieldIndex}
            mode={mode}
            activePage={activePage}
            onPageClick={scrollToPage}
          />
        ) : null}

        {/* v1 placement-redesign: delete confirmation popover, anchored
            next to the marker whose × was clicked. */}
        {pendingDelete && (
          <DeleteFieldConfirm
            anchorRect={pendingDelete.anchorRect}
            fieldLabel={pendingDelete.label}
            onConfirm={handleConfirmDelete}
            onCancel={() => setPendingDelete(null)}
          />
        )}

        {/* Save-to-template prompt: appears after a drag on a template-
            linked doc. Sits above the next-field banner; user can ignore
            it without losing the move (default = local-only). */}
        {pendingTemplateMove && linkedTemplateName && onSaveFieldToTemplate && (
          <SaveToTemplatePrompt
            fieldLabel={pendingTemplateMove.label}
            templateName={linkedTemplateName}
            onSaveToTemplate={async () => {
              const move = pendingTemplateMove;
              setPendingTemplateMove(null);
              try {
                await onSaveFieldToTemplate({
                  fieldId: move.fieldId,
                  rect: move.rect,
                });
              } catch (err) {
                console.error('[viewer] save-to-template failed', err);
                // Local override stays in place — user's drag is preserved.
              }
            }}
            onKeepLocal={() => setPendingTemplateMove(null)}
          />
        )}

        {/* Next field banner (or completion message) */}
        {showSequentialSigning && allSignerFieldsComplete ? (
          <div
            onClick={onAllFieldsComplete}
            style={{
              position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
              zIndex: 20, cursor: onAllFieldsComplete ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 16px', borderRadius: 20,
              backgroundColor: PDF_VIEWER.fieldSigned, color: '#fff',
              fontSize: 13, fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            All fields completed
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        ) : showSequentialSigning ? (
          <NextFieldBanner nextField={nextField} onNavigate={handleNextField} />
        ) : null}
      </div>
    </div>
  );
}

const FIELD_RAIL: CSSProperties = {
  width: 248,
  flexShrink: 0,
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRight: `1px solid ${APP.border}`,
  backgroundColor: APP.bgContent,
  fontFamily: APP.fontFamily,
  overflow: 'hidden',
};

const FIELD_RAIL_HEADER: CSSProperties = {
  padding: '12px 14px 10px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  borderBottom: `1px solid ${APP.border}`,
};

const FIELD_RAIL_TITLE: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: APP.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const FIELD_RAIL_HINT: CSSProperties = {
  fontSize: 11,
  lineHeight: '15px',
  color: APP.textSecondary,
};

const FIELD_RAIL_SIGNER: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  margin: '10px 12px 0',
  padding: '8px 9px',
  borderRadius: 7,
  border: `1px solid ${APP.border}`,
  backgroundColor: APP.bgCardBody,
  minWidth: 0,
};

const FIELD_RAIL_SIGNER_TEXT: CSSProperties = {
  fontSize: 11,
  lineHeight: '15px',
  color: APP.textPrimary,
  fontWeight: 650,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const FIELD_RAIL_SCROLL: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: 12,
};

const FIELD_RAIL_ACTIONS: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
  padding: 12,
  borderTop: `1px solid ${APP.border}`,
  backgroundColor: APP.bgContent,
};

const FIELD_RAIL_APPLY_BTN: CSSProperties = {
  width: '100%',
  border: 'none',
  borderRadius: 8,
  padding: '11px 12px',
  backgroundColor: PDF_VIEWER.fieldSigned,
  color: '#fff',
  fontFamily: APP.fontFamily,
  fontSize: 12,
  fontWeight: 800,
  cursor: 'pointer',
  boxShadow: APP.inputShadow,
};

const FIELD_RAIL_APPLY_BTN_DISABLED: CSSProperties = {
  backgroundColor: APP.bgContent,
  color: APP.textMuted,
  border: `1px solid ${APP.border}`,
  boxShadow: 'none',
  cursor: 'not-allowed',
  opacity: 0.66,
};

const FIELD_RAIL_ACTION_HINT: CSSProperties = {
  fontSize: 10,
  lineHeight: '14px',
  color: APP.textMuted,
};
